import { Alert, Button, CircularProgress, Collapse, Paper, Stack, Typography } from '@mui/material'
import { Suspense } from 'preact/compat'
import { useState } from 'preact/hooks'
import { lazy } from 'preact-iso'
import { api } from '../api/client'
import type { Assignment } from '../api/generated/Assignment'
import type { OfferReply } from '../api/generated/OfferReply'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { TimezoneNote } from '../shifts/TimezoneNote'
import { formatRange } from '../shifts/time'
import { CalendarExport } from './CalendarExport'
import { type AssignedShift, assignedShifts, reliefNotice } from './schedule'
import { AttendanceChip, OverlapWarning } from './status'
import { Withdraw } from './Withdraw'

// Split out with the Markdown renderer, which would grow the main bundle by a third
const JobDescription = lazy(() =>
  import('../positions/JobDescription').then((m) => m.JobDescription),
)

const presenceMessages = {
  not_found: "Ce n'est plus possible pour ce créneau.",
}

/** Attendance confirmation then check-in on site, from the shift's reminder (or start) until its end. */
function Presence({
  slug,
  assignment,
  shift,
  onChange,
}: {
  slug: string
  assignment: Assignment
  shift: Shift
  onChange: (assignments: Assignment[]) => void
}) {
  const { error, pending, run } = useAction(presenceMessages)
  const { attendance } = assignment
  const now = new Date()
  const open = new Date(shift.ends_at) > now
  if (attendance === 'not_requested' && !(open && new Date(shift.starts_at) <= now)) return null
  const post = (action: 'attendance' | 'check-in') =>
    run(async () =>
      onChange(
        await api.post<Assignment[]>(`/events/${slug}/assignments/${assignment.id}/${action}`, {}),
      ),
    )
  const relief = reliefNotice(assignment)
  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start', my: 1 }}>
      {attendance !== 'requested' && <AttendanceChip assignment={assignment} />}
      {attendance !== 'checked_in' && open && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {attendance === 'requested' && (
            <Button variant="contained" disabled={pending} onClick={() => post('attendance')}>
              Je serai là
            </Button>
          )}
          <Button
            variant={attendance === 'requested' ? 'outlined' : 'contained'}
            disabled={pending}
            onClick={() => post('check-in')}
          >
            Je suis à mon poste
          </Button>
        </Stack>
      )}
      {relief && open && (
        <Alert severity={assignment.relief === 'planned' ? 'info' : 'success'}>{relief}</Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  )
}

const offerMessages = {
  not_found: "Cette demande n'est plus valable.",
}

/** An organizer's request to take a shift, to accept or decline. */
function Offer({
  slug,
  entry: { assignment, shift, position },
  onChange,
}: {
  slug: string
  entry: AssignedShift
  onChange: (assignments: Assignment[]) => void
}) {
  const { error, pending, run } = useAction(offerMessages)
  const reply = (accept: boolean) =>
    run(async () => {
      const body: OfferReply = { accept }
      onChange(
        await api.post<Assignment[]>(`/events/${slug}/assignments/${assignment.id}/reply`, body),
      )
    })
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 'medium' }}>
        {formatRange(shift.starts_at, shift.ends_at)}
      </Typography>
      <Typography>{position.name}</Typography>
      {position.location && (
        <Typography variant="body2" color="text.secondary">
          {position.location}
        </Typography>
      )}
      <Typography variant="body2" sx={{ my: 1 }}>
        Les organisateurs vous demandent de prendre ce poste.
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" disabled={pending} onClick={() => reply(true)}>
          J'accepte
        </Button>
        <Button variant="outlined" disabled={pending} onClick={() => reply(false)}>
          Je refuse
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Paper>
  )
}

function ScheduledShift({
  slug,
  entry,
  deadlineHours,
  onChange,
}: {
  slug: string
  entry: AssignedShift
  deadlineHours: number
  onChange: (assignments: Assignment[]) => void
}) {
  const { assignment, shift, position } = entry
  const [showDescription, setShowDescription] = useState(false)
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 'medium' }}>
        {formatRange(shift.starts_at, shift.ends_at)}
      </Typography>
      <Typography>{position.name}</Typography>
      {position.location && (
        <Typography variant="body2" color="text.secondary">
          {position.location}
        </Typography>
      )}
      <Presence slug={slug} assignment={assignment} shift={shift} onChange={onChange} />
      <OverlapWarning assignment={assignment} />
      {position.job_description.trim() && (
        <>
          <Button size="small" onClick={() => setShowDescription(!showDescription)}>
            {showDescription ? 'Masquer la fiche de poste' : 'Voir la fiche de poste'}
          </Button>
          {/* Mounted while hidden, so the job description is loaded and cached for offline use */}
          <Collapse in={showDescription}>
            <Suspense fallback={<CircularProgress size={24} />}>
              <JobDescription
                markdown={position.job_description}
                slug={slug}
                positionId={position.id}
              />
            </Suspense>
          </Collapse>
        </>
      )}
      <Withdraw slug={slug} entry={entry} deadlineHours={deadlineHours} onChange={onChange} />
    </Paper>
  )
}

/** A volunteer's replacement requests, then accepted shifts with their job descriptions. */
export function VolunteerSpace({
  slug,
  timezone,
  withdrawalDeadlineHours,
}: {
  slug: string
  timezone: string
  withdrawalDeadlineHours: number
}) {
  const assignments = useApi<Assignment[]>(`/events/${slug}/assignments`)
  const shifts = useApi<Shift[]>(`/events/${slug}/shifts`)
  const positions = useApi<Position[]>(`/events/${slug}/positions`)
  const error = assignments.error ?? shifts.error ?? positions.error
  const entries =
    assignments.data &&
    shifts.data &&
    positions.data &&
    assignedShifts(assignments.data, shifts.data, positions.data)
  const schedule = entries?.filter((entry) => entry.assignment.status === 'accepted')
  const offers = entries?.filter((entry) => entry.assignment.status === 'offered')

  return (
    <>
      {offers && offers.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="h6">Demandes de remplacement</Typography>
          {offers.map((entry) => (
            <Offer
              key={entry.assignment.id}
              slug={slug}
              entry={entry}
              onChange={assignments.setData}
            />
          ))}
        </Stack>
      )}
      <Stack spacing={1}>
        <TimezoneNote timezone={timezone} />
        {error && <Alert severity="error">{error}</Alert>}
        {schedule?.length === 0 && (
          <Typography color="text.secondary">
            Aucun créneau pour l'instant : les organisateurs vous en affecteront selon vos
            disponibilités.
          </Typography>
        )}
        {schedule?.map((entry) => (
          <ScheduledShift
            key={entry.assignment.id}
            slug={slug}
            entry={entry}
            deadlineHours={withdrawalDeadlineHours}
            onChange={assignments.setData}
          />
        ))}
        {schedule && schedule.length > 0 && <CalendarExport slug={slug} />}
      </Stack>
    </>
  )
}
